import { createRoot, createSignal } from 'solid-js';
import type { IceType } from '~/types/game';

/** Starting ¤ balance for a new operator (design section 4: enough for a first
 *  purchase or two once shops open in step 5). Tunable. */
export const STARTING_CREDITS = 500;

/** Player-global client state: the ¤ balance and the learned-intel memory of
 *  target ICE types. Module-level singleton like the other lib stores. State
 *  is plain signals with a clear shape so a step-6 persistence layer can
 *  serialize/hydrate it without restructuring. In-memory only for now. */
function createPlayer() {
  const [credits, setCredits] = createSignal(STARTING_CREDITS);
  // target id → revealed iceType (learned on first hack; surfaced in prep).
  const [knownTypes, setKnownTypes] = createSignal<Record<string, IceType>>({});

  /** Apply a resolution's net ¤ effect (credit payout, debit penalty). */
  const applyCredits = (delta: number): void => {
    setCredits((c) => Math.max(0, c + delta));
  };

  /** Whether the player can afford a cost. */
  const canAfford = (cost: number): boolean => credits() >= cost;

  /** Spend ¤ on a purchase. Returns true and debits if affordable; otherwise a
   *  no-op returning false. The single ¤-sink entry point (tools, heat
   *  reduction). Earned-¤ only — never wire any spend to real money. */
  const spend = (cost: number): boolean => {
    if (cost < 0 || !canAfford(cost)) return false;
    setCredits((c) => c - cost);
    return true;
  };

  /** Record a target's revealed type after a hack reaches execution. */
  const learnType = (targetId: string, type: IceType): void => {
    setKnownTypes((m) => (m[targetId] === type ? m : { ...m, [targetId]: type }));
  };

  /** The known type for a target, or null if never hacked. */
  const knownType = (targetId: string): IceType | null => knownTypes()[targetId] ?? null;

  return { credits, knownTypes, applyCredits, canAfford, spend, learnType, knownType };
}

export const player = createRoot(() => createPlayer());
